"use client"

import { useState, useRef, memo } from "react"
import type { ChangeEvent, ReactNode } from "react"
import { Button } from "@/components/ui/button"
import { Slider } from "@/components/ui/slider"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  HighDefinition,
  RefreshCw,
  PlugsConnected,
  ScrollText,
  ChevronDown,
  ChevronUp,
  RotateCw,
  Eye,
  BookmarkPlus,
  Trash2,
  Glasses,
  Gauge,
  MoveVertical,
  Play,
  PlugZap,
  Search,
  Square,
  Headphones,
} from "@/lib/icons"
import { useVRStore } from "@/lib/vr-store"
import { projectionLabel, stereoLabel } from "@/lib/vr-utils"
import { useScriptStore } from "@/lib/script-store"
import { useDeviceStore } from "@/lib/device-store"
import { useManualModeStore } from "@/lib/manual-mode-store"
import { deviceController } from "@/lib/device-controller"
import { QuickConnectDeviceModal } from "@/components/quick-connect-device-modal"
import type { ProjectionType, StereoLayout } from "@/lib/vr-store"
import type { FunscriptAction, UserScript } from "@/lib/script-store"
import { useTranslations } from "next-intl"
import { playVRAudioCalibration } from "@/lib/vr-audio"
import { DeviceCalibrateButton } from "@/components/device-calibrate-button"

interface VRControlsProps {
  syncDelay: number
  onSyncDelayChange: (ms: number) => void
  activeScript?: UserScript | null
  onScriptSelect?: (script: UserScript) => void
  onScriptClear?: () => void
}

type PanelSection = "script" | "device" | "presets" | "view" | "optics" | "video" | "audio" | "special" | "sync"

// SliderRow and SectionHeaderBase MUST stay at module scope. Defined inside VRControlsImpl,
// each render produced a brand-new component *type*, so React unmounted and remounted every
// row — including a Radix Slider mid-drag, which then lost its pointer capture. That's why a
// slider would move once and then feel stuck, and why values snapped back to the incoming
// prop (which for most of these is exactly 50% of the range).
function SliderRow({
  label,
  value,
  min,
  max,
  step,
  suffix = "",
  onChange,
}: {
  label: string
  value: number
  min: number
  max: number
  step: number
  suffix?: string
  onChange: (v: number) => void
}) {
  return (
    <div className="space-y-1">
      <div className="flex min-w-0 justify-between gap-2 text-xs text-muted-foreground">
        <span className="min-w-0 pr-2">{label}</span>
        <span className="shrink-0 tabular-nums">{value}{suffix}</span>
      </div>
      {/* stopPropagation prevents ScrollArea from consuming the pointerdown
          before Radix Slider can call setPointerCapture */}
      <div onPointerDown={(e) => e.stopPropagation()}>
        <Slider
          value={[value]}
          min={min}
          max={max}
          step={step}
          onValueChange={([v]) => onChange(v)}
          className="w-full"
        />
      </div>
    </div>
  )
}

function SectionHeaderBase({
  id,
  label,
  icon,
  open,
  onToggle,
}: {
  id: PanelSection
  label: string
  icon: ReactNode
  open: boolean
  onToggle: (id: PanelSection) => void
}) {
  return (
    <button
      type="button"
      className="flex min-h-10 w-full touch-manipulation items-center justify-between gap-2 rounded-md bg-muted/60 px-2 py-2 text-left text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      onPointerDown={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onToggle(id)
      }}
    >
      <span className="flex min-w-0 items-center gap-2">{icon}<span className="truncate">{label}</span></span>
      {open ? <ChevronUp className="h-3 w-3 shrink-0" /> : <ChevronDown className="h-3 w-3 shrink-0" />}
    </button>
  )
}

function VRControlsImpl({ syncDelay, onSyncDelayChange, activeScript, onScriptSelect, onScriptClear }: VRControlsProps) {
  const t = useTranslations("vrControls")
  const [openSection, setOpenSection] = useState<PanelSection | null>(null)
  const [newPresetName, setNewPresetName] = useState("")
  const [showPresetInput, setShowPresetInput] = useState(false)
  const [scriptQuery, setScriptQuery] = useState("")
  const [connectingDeviceId, setConnectingDeviceId] = useState<string | null>(null)
  const [showQuickConnect, setShowQuickConnect] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const {
    projectionType,
    stereoLayout,
    transform,
    passthrough,
    greenscreen,
    keyColorPresets,
    audio,
    audioAvailable,
    presets,
    activePresetId,
    setProjectionType,
    setStereoLayout,
    setTransform,
    resetTransform,
    applyPreset,
    savePreset,
    deletePreset,
    setPassthrough,
    setGreenscreen,
    saveKeyColorPreset,
    deleteKeyColorPreset,
    setAudio,
    applyAudioPreset,
  } = useVRStore()
  const { scripts } = useScriptStore()
  const { devices, activeDeviceId, getActiveDevice, setActiveDevice, connectDevice } = useDeviceStore()
  const device = getActiveDevice()
  const {
    isActive: deviceAutoActive,
    speed: deviceAutoSpeed,
    strokeMin,
    strokeMax,
    setActive: setDeviceAutoActive,
    setSpeed: setDeviceAutoSpeed,
    setStrokeRange,
  } = useManualModeStore()

  const filteredScripts = scripts.filter((s) =>
    s.name.toLowerCase().includes(scriptQuery.toLowerCase()),
  )
  const deviceConnected = device?.isConnected ?? false
  const isVibrationDevice = device?.type === "lovense" || device?.type === "buttplug"
  const supportsStrokeRange = !!device && ["handy", "kiiroo", "osr2", "sr6"].includes(device.type)
  const strokeLength = strokeMax - strokeMin
  const deviceModeLabel =
    device?.type === "handy" ? "Handy HAMP / remote stroke mode" :
    device?.type === "lovense" || device?.type === "buttplug" ? "Vibration intensity mode" :
    device?.type === "kiiroo" ? "Stroke speed mode" :
    device?.type === "osr2" || device?.type === "sr6" ? "Multi-axis speed mode" :
    "Device auto mode"
  const devicePowerLabel = isVibrationDevice ? t("intensity") : t("speed")

  const toggle = (s: PanelSection) => setOpenSection((prev) => (prev === s ? null : s))

  const handleConnectDevice = async (deviceId: string) => {
    setConnectingDeviceId(deviceId)
    setActiveDevice(deviceId)
    try {
      await connectDevice(deviceId)
    } finally {
      setConnectingDeviceId(null)
    }
  }

  const loadLocalFunscript = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !onScriptSelect) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const json = JSON.parse(ev.target?.result as string)
        const actions: FunscriptAction[] = Array.isArray(json.actions) ? json.actions : []
        const script: UserScript = {
          id: `local_${Date.now()}`,
          name: file.name.replace(/\.funscript$/i, ""),
          description: "Loaded from local file",
          funscript: { version: json.version ?? "1.0", inverted: json.inverted, range: json.range, metadata: json.metadata, actions },
          tags: [],
          isPublished: false,
          deviceCompatibility: ["handy", "funscript"],
          averageIntensity: 50,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        }
        onScriptSelect(script)
      } catch {
        // Invalid local scripts are ignored here, matching the sidebar picker.
      } finally {
        e.target.value = ""
      }
    }
    reader.readAsText(file)
  }

  const toggleDeviceAuto = async () => {
    if (!deviceConnected) return
    if (deviceAutoActive) {
      await deviceController.manualStop()
      setDeviceAutoActive(false)
      return
    }
    const ok = await deviceController.manualStart(deviceAutoSpeed)
    if (ok) setDeviceAutoActive(true)
  }

  const handlePassthroughChange = (enabled: boolean) => {
    setPassthrough(enabled)
    if (enabled) setGreenscreen({ enabled: true })
  }

  const setDevicePowerValue = async (value: number[]) => {
    const next = value[0]
    setDeviceAutoSpeed(next)
    if (deviceAutoActive) await deviceController.manualSetSpeed(next)
  }

  const setStrokeLengthValue = async (value: number[]) => {
    const len = value[0]
    const center = (strokeMin + strokeMax) / 2
    const half = len / 2
    const nextMin = Math.max(0, Math.round(center - half))
    const nextMax = Math.min(100, Math.round(center + half))
    setStrokeRange(nextMin, nextMax)
    await deviceController.setStrokeRange(nextMin, nextMax)
  }

  const setStrokeMinValue = async (value: number[]) => {
    const next = Math.min(value[0], strokeMax - 5)
    setStrokeRange(next, strokeMax)
    await deviceController.setStrokeRange(next, strokeMax)
  }

  const setStrokeMaxValue = async (value: number[]) => {
    const next = Math.max(value[0], strokeMin + 5)
    setStrokeRange(strokeMin, next)
    await deviceController.setStrokeRange(strokeMin, next)
  }

  const SectionHeader = ({ id, label, icon }: { id: PanelSection; label: string; icon: ReactNode }) => (
    <SectionHeaderBase id={id} label={label} icon={icon} open={openSection === id} onToggle={toggle} />
  )

  return (
    // select-none on the whole panel: this is a control surface, not a document — there's no
    // legitimate reason to select its text — and without it, a drag starting on/near any
    // label (very easy to catch when aiming for a nearby slider or button) triggered the
    // browser's own native "drag the selected text" gesture instead of our controls. That
    // renders a free-floating drag ghost the browser lets go anywhere on the page, completely
    // unconstrained by this panel's own box — reported as "I can grab the menu or scrollable
    // areas and move all the UI around outside the container", which is exactly what a native
    // text/element drag looks like from the outside.
    <div className="w-full min-w-0 select-none space-y-2 overflow-x-hidden text-sm">
      {/* ── Script selection ───────────────────────────────────── */}
      <SectionHeader id="script" label={t("script")} icon={<ScrollText className="h-3.5 w-3.5" />} />
      {openSection === "script" && (
        <div className="pb-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">{t("current")}</span>
            {activeScript && onScriptClear && (
              <Button size="sm" variant="ghost" className="h-auto min-h-7 px-2 py-1 text-xs" onClick={onScriptClear}>
                {t("clear")}
              </Button>
            )}
          </div>
          {activeScript ? (
            <div className="rounded-md border bg-primary/5 p-2 text-xs">
              <p className="font-medium leading-tight break-words">{activeScript.name}</p>
              <p className="text-muted-foreground">{t("lengthActions", { length: activeScript.funscript.actions.length })}</p>
            </div>
          ) : (
            <p className="rounded-md border bg-muted/30 p-2 text-xs text-muted-foreground">
              {t("noScriptSelected")}
            </p>
          )}

          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
            <input
              value={scriptQuery}
              onChange={(e) => setScriptQuery(e.target.value)}
              placeholder={t("searchScripts")}
              className="h-8 w-full rounded border bg-background pl-7 pr-2 text-xs"
            />
          </div>

          <Button
            variant="outline"
            size="sm"
            className="h-auto min-h-9 w-full gap-1.5 whitespace-normal break-words px-2 py-1.5 text-xs leading-tight"
            onClick={() => fileInputRef.current?.click()}
            disabled={!onScriptSelect}
          >
            <ScrollText className="h-3 w-3 shrink-0" />
            {t("importFunscriptForThisVideo")}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".funscript,.json"
            aria-label={t("importFunscriptFile")}
            className="hidden"
            onChange={loadLocalFunscript}
          />

          <div className="max-h-48 space-y-1 overflow-y-auto pr-1">
            {filteredScripts.length === 0 && (
              <p className="py-3 text-center text-xs text-muted-foreground">
                {scripts.length === 0 ? t("noSavedScriptsYet") : t("noMatches")}
              </p>
            )}
            {filteredScripts.map((script) => (
              <button
                key={script.id}
                type="button"
                className="w-full rounded px-2 py-1.5 text-left text-xs transition-colors hover:bg-accent"
                onClick={() => onScriptSelect?.(script)}
              >
                <p className="font-medium leading-tight break-words">{script.name}</p>
                <p className="text-muted-foreground">{t("lengthActions", { length: script.funscript.actions.length })}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="border-t" />

      {/* ── Device auto / freeplay ─────────────────────────────── */}
      <SectionHeader id="device" label={t("deviceAuto")} icon={<PlugsConnected className="h-3.5 w-3.5" />} />
      {openSection === "device" && (
        <div className="pb-3 space-y-3">
          {!device ? (
            <div className="space-y-2 rounded-md border bg-muted/30 p-2">
              <p className="text-center text-xs text-muted-foreground">
                {devices.length === 0 ? t("noSavedDevicesYet") : t("chooseASavedDeviceToConnect")}
              </p>
              {/* Landing here without ever visiting the main Device tab first left no way
                  to add a device at all — this opens the same picker modal Device Manager
                  uses, so a first-time-here user isn't stuck. */}
              <Button
                type="button"
                size="sm"
                className="h-auto min-h-9 w-full gap-2 whitespace-normal"
                onClick={() => setShowQuickConnect(true)}
              >
                <PlugZap className="h-4 w-4" />
                {t("connectADevice")}
              </Button>
              {devices.map((savedDevice) => (
                <Button
                  key={savedDevice.id}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-auto min-h-9 w-full justify-between gap-2 whitespace-normal px-2 py-1.5 text-xs"
                  onClick={() => handleConnectDevice(savedDevice.id)}
                  disabled={connectingDeviceId === savedDevice.id}
                >
                  <span className="min-w-0 truncate">{savedDevice.name}</span>
                  <span className="shrink-0 text-[10px] capitalize text-muted-foreground">
                    {connectingDeviceId === savedDevice.id ? "Connecting..." : savedDevice.type}
                  </span>
                </Button>
              ))}
            </div>
          ) : !deviceConnected ? (
            <div className="space-y-2 rounded-md border bg-muted/30 p-2">
              <div className="flex min-w-0 items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-xs font-medium">{device.name}</p>
                  <p className="text-[10px] text-muted-foreground">{t("typeNotConnected", { type: device.type })}</p>
                </div>
                <Badge variant="secondary" className="shrink-0 text-[10px]">{t("offline")}</Badge>
              </div>
              {/* Was a direct handleConnectDevice(device.id) — silently retried whatever
                  device was last used, with no way to connect a different/new one from
                  here. Opens the same picker modal Device Manager uses instead; "Switch
                  device" below still covers quickly hopping between already-saved ones. */}
              <Button
                type="button"
                size="sm"
                className="h-auto min-h-9 w-full gap-2 whitespace-normal"
                onClick={() => setShowQuickConnect(true)}
              >
                <PlugZap className="h-4 w-4" />
                {t("connectDevice")}
              </Button>
              {devices.length > 1 && (
                <div className="space-y-1 border-t pt-2">
                  <p className="text-[10px] text-muted-foreground">{t("switchDevice")}</p>
                  {devices.filter((savedDevice) => savedDevice.id !== device.id).map((savedDevice) => (
                    <Button
                      key={savedDevice.id}
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-auto min-h-8 w-full justify-between gap-2 whitespace-normal px-2 py-1 text-xs"
                      onClick={() => handleConnectDevice(savedDevice.id)}
                      disabled={connectingDeviceId === savedDevice.id}
                    >
                      <span className="min-w-0 truncate">{savedDevice.name}</span>
                      <span className="shrink-0 text-[10px] capitalize text-muted-foreground">
                        {connectingDeviceId === savedDevice.id ? "Connecting..." : savedDevice.type}
                      </span>
                    </Button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="flex min-w-0 items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-xs font-medium">{device.name}</p>
                  <p className="text-[10px] text-muted-foreground">{deviceModeLabel}</p>
                </div>
                <Badge variant={deviceAutoActive ? "default" : "secondary"} className="shrink-0 text-[10px]">
                  {deviceAutoActive ? t("active") : t("idle")}
                </Badge>
              </div>

              <Button
                className={`h-auto min-h-9 w-full gap-2 whitespace-normal ${deviceAutoActive ? "bg-red-600 text-white hover:bg-red-700" : ""}`}
                onClick={toggleDeviceAuto}
              >
                {deviceAutoActive ? <Square className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                {deviceAutoActive ? t("stopDevice") : isVibrationDevice ? t("startVibration") : t("startAutoMode")}
              </Button>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-1 text-muted-foreground"><Gauge className="h-3 w-3" />{devicePowerLabel}</span>
                  <span className="tabular-nums font-medium">{deviceAutoSpeed}%</span>
                </div>
                <Slider value={[deviceAutoSpeed]} min={0} max={100} step={1} onValueChange={setDevicePowerValue} />
                <div className="grid grid-cols-4 gap-1">
                  {[25, 50, 75, 100].map((value) => (
                    <Button
                      key={value}
                      size="sm"
                      variant={deviceAutoSpeed === value ? "default" : "outline"}
                      className="h-auto min-h-8 px-1 text-xs"
                      onClick={() => setDevicePowerValue([value])}
                    >
                      {value}%
                    </Button>
                  ))}
                </div>
              </div>

              {supportsStrokeRange && (
                <>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1 text-muted-foreground"><MoveVertical className="h-3 w-3" />{t("strokeLength")}</span>
                      <span className="tabular-nums font-medium">{strokeLength}%</span>
                    </div>
                    <Slider value={[strokeLength]} min={5} max={100} step={1} onValueChange={setStrokeLengthValue} />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                        <span>{t("bottom")}</span>
                        <span>{strokeMin}%</span>
                      </div>
                      <Slider value={[strokeMin]} min={0} max={95} step={1} onValueChange={setStrokeMinValue} />
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                        <span>{t("top")}</span>
                        <span>{strokeMax}%</span>
                      </div>
                      <Slider value={[strokeMax]} min={5} max={100} step={1} onValueChange={setStrokeMaxValue} />
                    </div>
                  </div>
                </>
              )}

              {devices.length > 1 && (
                <div className="space-y-1 border-t pt-2">
                  <p className="text-[10px] text-muted-foreground">{t("switchDevice")}</p>
                  {devices.filter((savedDevice) => savedDevice.id !== activeDeviceId).map((savedDevice) => (
                    <Button
                      key={savedDevice.id}
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-auto min-h-8 w-full justify-between gap-2 whitespace-normal px-2 py-1 text-xs"
                      onClick={() => handleConnectDevice(savedDevice.id)}
                      disabled={connectingDeviceId === savedDevice.id}
                    >
                      <span className="min-w-0 truncate">{savedDevice.name}</span>
                      <span className="shrink-0 text-[10px] capitalize text-muted-foreground">
                        {connectingDeviceId === savedDevice.id ? "Connecting..." : savedDevice.type}
                      </span>
                    </Button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div className="border-t" />

      {/* ── Script sync ─────────────────────────────────────────── */}
      {/* Moved up next to Device Auto — the two are used together (syncing a script to a
          connected device), so having them adjacent makes more sense than sync sitting at
          the very end after everything else. */}
      <SectionHeader id="sync" label={t("scriptSync")} icon={<RefreshCw className="h-3.5 w-3.5" />} />
      {openSection === "sync" && (
        <div className="pb-3 space-y-3">
          <SliderRow
            label={t("scriptDelay")}
            value={syncDelay}
            min={-5000}
            max={5000}
            step={50}
            suffix="ms"
            onChange={onSyncDelayChange}
          />
          <DeviceCalibrateButton />
          <div className="grid grid-cols-3 gap-1">
            <Button size="sm" variant="outline" className="h-auto min-h-9 px-1 text-xs whitespace-normal leading-tight" onClick={() => onSyncDelayChange(syncDelay - 50)}>{t("n50ms")}</Button>
            <Button size="sm" variant="outline" className="h-auto min-h-9 px-1 text-xs whitespace-normal leading-tight" onClick={() => onSyncDelayChange(0)}>{t("reset")}</Button>
            <Button size="sm" variant="outline" className="h-auto min-h-9 px-1 text-xs whitespace-normal leading-tight" onClick={() => onSyncDelayChange(syncDelay + 50)}>{t("n50ms2")}</Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("negativeDeviceFiresEarlierPositiveDevi")}
          </p>
        </div>
      )}

      <div className="border-t" />

      {/* ── Presets ─────────────────────────────────────────────── */}
      <SectionHeader id="presets" label={t("presets")} icon={<BookmarkPlus className="h-3.5 w-3.5" />} />
      {openSection === "presets" && (
        <div className="pb-3 space-y-2">
          <div className="grid grid-cols-[repeat(auto-fit,minmax(8.5rem,1fr))] gap-1.5">
            {presets.map((p) => (
              <div key={p.id} className="flex min-w-0 items-stretch gap-1">
                <Button
                  size="sm"
                  variant={activePresetId === p.id ? "default" : "outline"}
                  className="h-auto min-h-9 min-w-0 flex-1 whitespace-normal break-words px-2 py-1.5 text-xs leading-tight"
                  onClick={() => applyPreset(p.id)}
                >
                  {p.name}
                </Button>
                {!p.isBuiltIn && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 shrink-0"
                    onClick={() => deletePreset(p.id)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          {showPresetInput ? (
            <div className="flex flex-wrap gap-1">
              <input
                autoFocus
                value={newPresetName}
                onChange={(e) => setNewPresetName(e.target.value)}
                placeholder={t("presetName")}
                className="h-8 min-w-36 flex-1 rounded border bg-background px-2 text-xs"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newPresetName.trim()) {
                    savePreset(newPresetName.trim())
                    setNewPresetName("")
                    setShowPresetInput(false)
                  }
                  if (e.key === "Escape") setShowPresetInput(false)
                }}
              />
              <Button
                size="sm"
                className="h-auto min-h-8 whitespace-normal px-3 py-1 text-xs"
                onClick={() => {
                  if (newPresetName.trim()) {
                    savePreset(newPresetName.trim())
                    setNewPresetName("")
                    setShowPresetInput(false)
                  }
                }}
              >
                {t("save")}
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="h-auto min-h-9 w-full whitespace-normal break-words px-2 py-1.5 text-xs leading-tight"
              onClick={() => setShowPresetInput(true)}
            >
              {t("saveCurrentAsPreset")}
            </Button>
          )}
        </div>
      )}

      <div className="border-t" />

      {/* ── View transforms ─────────────────────────────────────── */}
      <SectionHeader id="view" label={t("viewTransform")} icon={<RotateCw className="h-3.5 w-3.5" />} />
      {openSection === "view" && (
        <div className="pb-3 space-y-3">
          <SliderRow label={t("rollLyingDown")} value={transform.roll} min={-180} max={180} step={1} suffix="°"
            onChange={(v) => setTransform({ roll: v })} />
          <SliderRow label={t("pitchUpDown")} value={transform.pitch} min={-90} max={90} step={1} suffix="°"
            onChange={(v) => setTransform({ pitch: v })} />
          <SliderRow label={t("yawLeftRight")} value={transform.yaw} min={-180} max={180} step={1} suffix="°"
            onChange={(v) => setTransform({ yaw: v })} />
          <SliderRow label={t("heightOffset")} value={transform.height} min={-1} max={1} step={0.01}
            onChange={(v) => setTransform({ height: v })} />
          <SliderRow label={t("horizontalOffset")} value={transform.positionX} min={-3} max={3} step={0.05}
            onChange={(v) => setTransform({ positionX: v })} />
          <SliderRow label={t("fieldOfView")} value={transform.fov} min={60} max={120} step={1} suffix="°"
            onChange={(v) => setTransform({ fov: v })} />
          <Button size="sm" variant="ghost" className="h-auto min-h-9 w-full whitespace-normal break-words px-2 py-1.5 text-xs leading-tight" onClick={resetTransform}>
            {t("resetAllTransforms")}
          </Button>
        </div>
      )}

      <div className="border-t" />

      {/* ── Optics / stereo ─────────────────────────────────────── */}
      <SectionHeader id="optics" label={t("opticsStereo")} icon={<Eye className="h-3.5 w-3.5" />} />
      {openSection === "optics" && (
        <div className="pb-3 space-y-3">
          <SliderRow label="IPD" value={transform.ipd} min={55} max={75} step={0.5} suffix="mm"
            onChange={(v) => setTransform({ ipd: v })} />
          <SliderRow label={t("depthStrength")} value={transform.depthStrength * 100} min={25} max={175} step={1} suffix="%"
            onChange={(v) => setTransform({ depthStrength: v / 100 })} />
          <SliderRow label={t("convergence")} value={transform.convergence * 100} min={-10} max={10} step={0.1} suffix="%"
            onChange={(v) => setTransform({ convergence: v / 100 })} />
          <SliderRow label={t("verticalAlignment")} value={transform.verticalAlignment * 100} min={-10} max={10} step={0.1} suffix="%"
            onChange={(v) => setTransform({ verticalAlignment: v / 100 })} />
          <div className="flex items-center justify-between">
            <Label className="min-w-0 pr-3 text-xs leading-tight">{t("swapEyes")}</Label>
            <Switch checked={transform.swapEyes} onCheckedChange={(swapEyes) => setTransform({ swapEyes })} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("projection")}</Label>
            <Select value={projectionType} onValueChange={(v) => setProjectionType(v as ProjectionType)}>
              <SelectTrigger className="h-auto min-h-9 w-full min-w-0 text-xs [&>span]:truncate">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[70]">
                <SelectItem value="equirectangular_180">{t("n180Equirectangular")}</SelectItem>
                <SelectItem value="equirectangular_360">{t("n360Equirectangular")}</SelectItem>
                <SelectItem value="fisheye_180">{t("n180Fisheye")}</SelectItem>
                <SelectItem value="mkx200">{t("mKX200Fisheye")}</SelectItem>
                <SelectItem value="mkx220">{t("mKX220Fisheye")}</SelectItem>
                <SelectItem value="flat">{t("flatCinemaScreen")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("sourceLayout")}</Label>
            <Select value={stereoLayout} onValueChange={(v) => setStereoLayout(v as StereoLayout)}>
              <SelectTrigger className="h-auto min-h-9 w-full min-w-0 text-xs [&>span]:truncate">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[70]">
                <SelectItem value="mono">{t("monoSource")}</SelectItem>
                <SelectItem value="sbs">{t("sideBySideSource")}</SelectItem>
                <SelectItem value="tb">{t("topBottomSource")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {projectionType === "flat" && (
            <>
              <SliderRow label={t("screenDistance")} value={transform.screenDistance} min={2} max={20} step={0.1}
                onChange={(v) => setTransform({ screenDistance: v })} />
              <SliderRow label={t("screenSize")} value={transform.screenScale * 100} min={50} max={200} step={1} suffix="%"
                onChange={(v) => setTransform({ screenScale: v / 100 })} />
              <SliderRow label={t("screenCurve")} value={transform.curvature * 100} min={0} max={100} step={1} suffix="%"
                onChange={(v) => setTransform({ curvature: v / 100 })} />
            </>
          )}
          <SliderRow label={t("bulgePinch")} value={transform.bulge * 100} min={-200} max={200} step={1} suffix="%"
            onChange={(v) => setTransform({ bulge: v / 100 })} />
          <Button size="sm" variant="ghost" className="h-auto min-h-9 w-full whitespace-normal break-words px-2 py-1.5 text-xs leading-tight" onClick={resetTransform}>
            {t("resetAllTransforms")}
          </Button>
        </div>
      )}

      <div className="border-t" />

      {/* ── Video adjustments ────────────────────────────────────── */}
      <SectionHeader id="video" label={t("video")} icon={<HighDefinition className="h-3.5 w-3.5" />} />
      {openSection === "video" && (
        <div className="pb-3 space-y-3">
          <SliderRow label={t("brightness")} value={transform.brightness} min={0} max={200} step={1} suffix="%"
            onChange={(v) => setTransform({ brightness: v })} />
          <SliderRow label={t("contrast")} value={transform.contrast} min={0} max={200} step={1} suffix="%"
            onChange={(v) => setTransform({ contrast: v })} />
          <SliderRow label={t("saturation")} value={transform.saturation} min={0} max={200} step={1} suffix="%"
            onChange={(v) => setTransform({ saturation: v })} />
          <SliderRow label={t("sharpness")} value={transform.sharpness} min={0} max={100} step={1} suffix="%"
            onChange={(v) => setTransform({ sharpness: v })} />
          <Button size="sm" variant="ghost" className="h-auto min-h-9 w-full whitespace-normal break-words px-2 py-1.5 text-xs leading-tight" onClick={resetTransform}>
            {t("resetAllTransforms")}
          </Button>
        </div>
      )}

      <div className="border-t" />

      {/* ── Audio processing / spatialization ─────────────────── */}
      <SectionHeader id="audio" label={t("audio")} icon={<Headphones className="h-3.5 w-3.5" />} />
      {openSection === "audio" && (
        <div className="space-y-3 pb-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-xs">{t("audioMode")}</Label>
              {audioAvailable !== null && (
                <Badge variant={audioAvailable ? "secondary" : "outline"} className="text-[10px]">
                  {audioAvailable ? t("webAudioActive") : t("nativeAudioOnly")}
                </Badge>
              )}
            </div>
            <Select value={audio.mode} onValueChange={(mode) => setAudio({ mode: mode as typeof audio.mode })}>
              <SelectTrigger className="h-auto min-h-9 w-full min-w-0 text-xs [&>span]:truncate">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[70]">
                <SelectItem value="original">{t("original")}</SelectItem>
                <SelectItem value="enhanced">{t("enhancedStereo")}</SelectItem>
                <SelectItem value="front">{t("frontAnchored")}</SelectItem>
                <SelectItem value="positioned">{t("n3DPosition")}</SelectItem>
                <SelectItem value="upmix">{t("spatialUpmix")}</SelectItem>
                <SelectItem value="ambisonic">{t("fOAAmbisonic")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-3 gap-1">
            {([
              ["original", "Original"],
              ["clear", "Clear"],
              ["immersive", "Immersive"],
              ["upmix", "Upmix"],
              ["close", "Close"],
              ["room", "Room"],
            ] as const).map(([id, label]) => (
              <Button key={id} size="sm" variant="outline" className="h-auto min-h-8 px-1 text-xs" onClick={() => applyAudioPreset(id)}>
                {label}
              </Button>
            ))}
          </div>

          <Button
            size="sm"
            variant="secondary"
            className="h-auto min-h-9 w-full gap-2 whitespace-normal text-xs"
            disabled={audioAvailable !== true}
            onClick={() => playVRAudioCalibration(audio)}
            title={t("frontRightBackLeftThenAbove")}
          >
            <Headphones className="h-3.5 w-3.5" />
            {t("runSpatialAudioTest")}
          </Button>

          {audio.mode !== "original" && (
            <>
              <div className="flex items-center justify-between gap-2">
                <Label className="text-xs">{t("safeGain")}</Label>
                <Badge variant="secondary" className="text-[10px]">{t("limiterOn")}</Badge>
              </div>
              <SliderRow label={t("volumeBoost")} value={audio.boostDb} min={0} max={12} step={0.5} suffix=" dB"
                onChange={(boostDb) => setAudio({ boostDb })} />
              <SliderRow label={t("bass")} value={audio.bass} min={-12} max={12} step={0.5} suffix=" dB"
                onChange={(bass) => setAudio({ bass })} />
              <SliderRow label={t("mid")} value={audio.mid} min={-12} max={12} step={0.5} suffix=" dB"
                onChange={(mid) => setAudio({ mid })} />
              <SliderRow label={t("treble")} value={audio.treble} min={-12} max={12} step={0.5} suffix=" dB"
                onChange={(treble) => setAudio({ treble })} />
              <SliderRow label={t("balance")} value={Math.round(audio.balance * 100)} min={-100} max={100} step={1} suffix="%"
                onChange={(balance) => setAudio({ balance: balance / 100 })} />
              <SliderRow label={t("stereoWidth")} value={Math.round(audio.stereoWidth * 100)} min={0} max={200} step={1} suffix="%"
                onChange={(stereoWidth) => setAudio({ stereoWidth: stereoWidth / 100 })} />
              <SliderRow label={t("crossfeed")} value={Math.round(audio.crossfeed * 100)} min={0} max={50} step={1} suffix="%"
                onChange={(crossfeed) => setAudio({ crossfeed: crossfeed / 100 })} />
              <SliderRow label={t("roomAmount")} value={Math.round(audio.roomMix * 100)} min={0} max={60} step={1} suffix="%"
                onChange={(roomMix) => setAudio({ roomMix: roomMix / 100 })} />
              {audio.roomMix > 0 && (
                <SliderRow label={t("roomSize")} value={Math.round(audio.roomSize * 100)} min={5} max={100} step={1} suffix="%"
                  onChange={(roomSize) => setAudio({ roomSize: roomSize / 100 })} />
              )}
            </>
          )}

          {(audio.mode === "front" || audio.mode === "positioned" || audio.mode === "upmix" || audio.mode === "ambisonic") && (
            <div className="flex items-center justify-between border-t pt-3">
              <Label className="min-w-0 pr-3 text-xs leading-tight">{t("headTracking")}</Label>
              <Switch checked={audio.headTracking} onCheckedChange={(headTracking) => setAudio({ headTracking })} />
            </div>
          )}

          {(audio.mode === "front" || audio.mode === "positioned") && (
            <div className="space-y-3 border-t pt-3">
              <SliderRow label={t("spatialAmount")} value={Math.round(audio.spatialMix * 100)} min={0} max={100} step={1} suffix="%"
                onChange={(spatialMix) => setAudio({ spatialMix: spatialMix / 100 })} />
              {audio.mode === "positioned" && (
                <>
                  <SliderRow label={t("horizontalAngle")} value={audio.azimuth} min={-180} max={180} step={1} suffix="°"
                    onChange={(azimuth) => setAudio({ azimuth })} />
                  <SliderRow label={t("elevation")} value={audio.elevation} min={-90} max={90} step={1} suffix="°"
                    onChange={(elevation) => setAudio({ elevation })} />
                </>
              )}
              <SliderRow label={t("distance")} value={audio.distance} min={0.5} max={10} step={0.1} suffix="m"
                onChange={(distance) => setAudio({ distance })} />
              <SliderRow label={t("distanceFalloff")} value={Math.round(audio.rolloff * 100)} min={0} max={200} step={5} suffix="%"
                onChange={(rolloff) => setAudio({ rolloff: rolloff / 100 })} />
            </div>
          )}

          {audio.mode === "upmix" && (
            <div className="space-y-3 border-t pt-3">
              <SliderRow label={t("soundstageSpread")} value={audio.upmixSpread} min={10} max={120} step={1} suffix="°"
                onChange={(upmixSpread) => setAudio({ upmixSpread })} />
              <SliderRow label={t("rearAmbience")} value={Math.round(audio.upmixRear * 100)} min={0} max={50} step={1} suffix="%"
                onChange={(upmixRear) => setAudio({ upmixRear: upmixRear / 100 })} />
            </div>
          )}

          {audio.mode === "ambisonic" && (
            <div className="space-y-2 border-t pt-3">
              <Label className="text-xs">{t("fOAChannelFormat")}</Label>
              <Select value={audio.ambisonicOrder} onValueChange={(ambisonicOrder) => setAudio({ ambisonicOrder: ambisonicOrder as typeof audio.ambisonicOrder })}>
                <SelectTrigger className="h-auto min-h-9 w-full min-w-0 text-xs [&>span]:truncate">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[70]">
                  <SelectItem value="ambix">{t("ambiXWYZX")}</SelectItem>
                  <SelectItem value="fuma">{t("fuMaWXYZ")}</SelectItem>
                </SelectContent>
              </Select>
              <Badge variant="outline" className="h-auto whitespace-normal text-[10px] leading-tight">
                {t("requiresAFourChannelFirstOrderAmbisonicSo")}
              </Badge>
            </div>
          )}
        </div>
      )}

      <div className="border-t" />

      {/* ── Special modes ────────────────────────────────────────── */}
      <SectionHeader id="special" label={t("specialModes")} icon={<Glasses className="h-3.5 w-3.5" />} />
      {openSection === "special" && (
        <div className="pb-3 space-y-3">
          <div className="flex items-center justify-between">
            <Label className="min-w-0 pr-3 text-xs leading-tight">{t("passthrough")}</Label>
            <Switch checked={passthrough} onCheckedChange={handlePassthroughChange} />
          </div>
          {passthrough && (
            <p className="text-xs text-muted-foreground">{t("requiresQuest3ProWithColorPassthrough")}</p>
          )}
          <div className="border-t pt-2 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="min-w-0 pr-3 text-xs leading-tight">{t("greenscreenChromaKey")}</Label>
              <Switch
                checked={greenscreen.enabled}
                onCheckedChange={(v) => setGreenscreen({ enabled: v })}
              />
            </div>
            {greenscreen.enabled && (
              <>
                <div className="flex items-center gap-2">
                  <Label className="text-xs w-16 shrink-0 leading-tight">{t("keyColor")}</Label>
                  <input
                    type="color"
                    value={greenscreen.color}
                    onChange={(e) => setGreenscreen({ color: e.target.value })}
                    className="h-7 w-12 cursor-pointer rounded border"
                  />
                  {!keyColorPresets.includes(greenscreen.color) && keyColorPresets.length < 4 && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 gap-1 px-2 text-xs"
                      onClick={() => saveKeyColorPreset(greenscreen.color)}
                    >
                      <BookmarkPlus className="h-3 w-3" />
                      {t("save")}
                    </Button>
                  )}
                </div>
                {keyColorPresets.length > 0 && (
                  <div className="flex items-center gap-2">
                    {keyColorPresets.map((c) => (
                      <div key={c} className="group relative">
                        <button
                          type="button"
                          className="h-8 w-8 rounded border-2"
                          style={{ backgroundColor: c, borderColor: greenscreen.color === c ? "var(--ring)" : "transparent" }}
                          onClick={() => setGreenscreen({ color: c })}
                          title={c}
                        />
                        <button
                          type="button"
                          aria-label={t("deletePreset")}
                          className="absolute -right-1 -top-1 hidden h-4 w-4 items-center justify-center rounded-full bg-destructive text-destructive-foreground group-hover:flex"
                          onClick={() => deleteKeyColorPreset(c)}
                        >
                          <Trash2 className="h-2.5 w-2.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <SliderRow label={t("similarityTolerance")} value={greenscreen.threshold} min={0} max={100} step={1} suffix="%"
                  onChange={(v) => setGreenscreen({ threshold: v })} />
                <SliderRow label={t("smoothnessFalloff")} value={greenscreen.smoothness} min={0} max={100} step={1} suffix="%"
                  onChange={(v) => setGreenscreen({ smoothness: v })} />
                <SliderRow label={t("spillSuppression")} value={greenscreen.spill} min={0} max={100} step={1} suffix="%"
                  onChange={(v) => setGreenscreen({ spill: v })} />
              </>
            )}
          </div>
        </div>
      )}

      <QuickConnectDeviceModal open={showQuickConnect} onClose={() => setShowQuickConnect(false)} />
    </div>
  )
}

// The panel's own state (device connection, transform sliders, audio, presets) comes from
// useVRStore/useDeviceStore subscriptions, not from these props — so it only actually needs
// to re-render when those stores change or when syncDelay/activeScript genuinely change. Its
// parent (VROverlay) re-renders on every video `timeupdate`, many times a second, to track
// the seek bar; without this memo, that dragged this whole sidebar (every SliderRow, the
// ScrollArea, script list) into a full re-render on the same schedule, which is real
// main-thread contention against whatever the *user* is actively dragging — a slider handle
// feeling "stuck after the first tick" and the seek bar itself feeling choppy are the exact
// symptom of losing the CPU mid-drag to unrelated re-renders. Confirmed live.
export const VRControls = memo(VRControlsImpl)
